import { defineStore } from 'pinia'
import { reactive, ref, computed } from 'vue'
import { vistraService } from '@/services/vistra.service'
import { dialog, i18N } from '@/common/util'

import type { LoaderType, VistraPanelsType } from '@/types/types'

export const useVistraStore = defineStore('vistra', () => {
  const list = ref<VistraPanelsType[]>([])

  const status = reactive<Record<string, LoaderType>>({
    getAll: 'initial',
    create: 'initial',
    update: 'initial',
    delete: 'initial',
    getById: 'initial',
    loginVistra: 'initial'
  })

  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  const vistraToken = ref('')
  const vistraRefreshToken = ref('')

  // Endpoints Plantar
  const getAll = async () => {
    status.getAll = 'loading'

    try {
      const result = await vistraService.get<any[]>()
      list.value = result.data.map(el => formatProps(el, true)) as VistraPanelsType[]
      status.getAll = 'success'
      return result
    } catch (error) {
      console.error('Error fetching Vistra panels:', error)
      status.getAll = 'error'
    }
  }

  const create = async (obj: VistraPanelsType) => {
    status.create = 'loading'
    try {
      const {data, success} = await vistraService.post(undefined, formatProps(obj))

      if (success) {
        getAll()
      }

      status.create = 'success'

      return data
    } catch (error) {
      console.error('Error creating Vistra panel:', error)
      status.create = 'error'
    }
  }

  const update = async (id: string, obj: VistraPanelsType) => {
    status.update = 'loading'
    try {
      const {data, success} = await vistraService.put(id, formatProps(obj))
      if (success) getAll()
      status.update = 'success'
      return data
    } catch (error) {
      console.error('Error updating Vistra panel:', error)
      status.update = 'error'
    }
  }

  const remove = async (obj: VistraPanelsType, force?: boolean) => {
    if (!force) {
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', { type: i18N('menu.biPanels').toLowerCase(), name: obj.name }),
        complete: () => remove(obj, true)
      })
    }

    try {
      status.delete = 'loading'
      const { success } = await vistraService.delete(obj.id)
      if (success) {
        getAll()
      }

      status.delete = 'success'
      return success
    } catch {
      status.delete = 'error'
    } 
  }

  const clear = () => {
    vistraToken.value = ''
    vistraRefreshToken.value = ''
  }

  const getById = async (id: string) => {
    status.getById = 'loading'
    try {
      const { data } = await vistraService.get(id)

      status.getById = 'success'
      return formatProps(data, true)
    } catch (error) {
      console.error('Error fetching Vistra panel by ID:', error)
      status.getById = 'error'
    }
  }

  const formatProps = (obj: any, isResponse: boolean = false) => {
    return (
      isResponse
        ? {
          ...(obj.id ? {id: obj.id} : {}),
          name: obj.painelNome,
          code: obj.painelId,
          public: obj.publico,
          contracts: obj.contratosIds,
          active: obj.ativo,
          parameters: obj.parametros,
          classification: obj.classificacao,
          panelType: obj.tipo ?? 'Painel'
        } : {
          ...(obj.id ? {id: obj.id} : {}),
          painelNome: obj.name,
          painelId: obj.code,
          publico: obj.public,
          contratosIds: obj.contracts,
          ativo: obj.active,
          parametros: obj.parameters,
          classificacao: obj.classification,
          tipo: obj.panelType
        }
    )
  }

  // Endpoints Vistra
  const loginVistra = async () => {
    status.loginVistra = 'loading'

    try {
      clear()
      const { data } = await vistraService.login() 

      if (data?.authToken) {
        vistraToken.value = data?.authToken
        vistraRefreshToken.value = data?.refreshToken
      }

      status.loginVistra = 'success'
      return true
    } catch (error) {
      console.error('Error logging into Vistra:', error)
      status.loginVistra = 'error'
    }
  }

  return {
    // States Plantar
    list,
    status,
    // Actions Plantar
    getAll,
    create,
    update,
    remove,
    getById,
    // States Vistra
    vistraToken,
    vistraRefreshToken,
    // Actions Vistra
    loginVistra,
    //Getters
    loading
  }
})
